import { useQuery } from '@tanstack/react-query';
import type { Candle, Timeframe } from '@pipyar/core';
import { api } from '@/lib/api';

export type LevelKind = 'poc' | 'vah' | 'val' | 'lvn' | 'hvn';

export interface ProfileLevel {
  price: number;
  kind: LevelKind;
  volume: number;
  strength: number;
  distance: number;
  side: 'above' | 'below' | 'at';
  score: number;
}

export interface ProfileBin {
  low: number;
  high: number;
  mid: number;
  volume: number;
  share: number;
  inValueArea: boolean;
}

export interface VolumeProfile {
  bins: ProfileBin[];
  binSize: number;
  total: number;
  poc: number;
  vah: number;
  val: number;
  valueAreaPct: number;
  bars: number;
  from: number;
  to: number;
  levels: ProfileLevel[];
}

export interface VolumeResponse {
  symbol: string;
  timeframe: Timeframe;
  digits: number;
  storedGmtOffsetSec: number;
  requestedBars: number;
  candles: Candle[];
  profile: VolumeProfile | null;
}

export interface VolumeSummaryRow {
  timeframe: Timeframe;
  price: number;
  poc: number;
  vah: number;
  val: number;
  bars: number;
  best: ProfileLevel | null;
}

export interface VolumeSummaryResponse {
  symbol: string;
  digits: number;
  serverTime: number;
  timeframes: VolumeSummaryRow[];
}

export function useVolumeProfile(
  symbol: string | undefined,
  timeframe: Timeframe,
  opts: { bins: number },
) {
  return useQuery({
    queryKey: ['volume-profile', symbol, timeframe, opts.bins],
    enabled: Boolean(symbol),
    // The profile only moves as bars close, so it does not need the five-second
    // refresh the price-following widgets use.
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<VolumeResponse>(
        `/api/market/volume-profile?symbol=${encodeURIComponent(symbol!)}` +
          `&timeframe=${timeframe}&bins=${opts.bins}`,
      ),
  });
}

export function useVolumeSummary(symbol: string | undefined) {
  return useQuery({
    queryKey: ['volume-profile-summary', symbol],
    enabled: Boolean(symbol),
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<VolumeSummaryResponse>(
        `/api/market/volume-profile/summary?symbol=${encodeURIComponent(symbol!)}`,
      ),
  });
}
